import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { translations as defaultTranslations, LANGUAGES, DEFAULT_LANGUAGE } from '../utils/translations';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

// Apply dot-notation override on a nested object: setByPath(obj, "home.heroTitle1", "value")
const setByPath = (obj, path, value) => {
  const keys = path.split('.');
  let cur = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    if (typeof cur[keys[i]] !== 'object' || cur[keys[i]] === null) {
      cur[keys[i]] = {};
    }
    cur = cur[keys[i]];
  }
  cur[keys[keys.length - 1]] = value;
};

// Deep clone defaults and apply overrides
const buildTranslations = (overrides) => {
  // Deep-clone defaults
  const merged = JSON.parse(JSON.stringify(defaultTranslations));
  for (const lang of Object.keys(overrides || {})) {
    if (!merged[lang]) continue;
    for (const key of Object.keys(overrides[lang] || {})) {
      setByPath(merged[lang], key, overrides[lang][key]);
    }
  }
  return merged;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('fabrikov_language');
    if (saved && LANGUAGES.find((l) => l.code === saved)) {
      return saved;
    }
    return DEFAULT_LANGUAGE;
  });

  const [activeTranslations, setActiveTranslations] = useState(defaultTranslations);

  useEffect(() => {
    localStorage.setItem('fabrikov_language', language);
    document.documentElement.lang = language;
  }, [language]);

  const refreshTranslations = async () => {
    try {
      const { data } = await axios.get('/api/translations');
      setActiveTranslations(buildTranslations(data));
    } catch (e) {
      // Fall back to defaults silently
      setActiveTranslations(defaultTranslations);
    }
  };

  useEffect(() => {
    refreshTranslations();
  }, []);

  // Translation function with dot-notation key support
  const t = (key) => {
    const keys = key.split('.');
    let value = activeTranslations[language];
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to Czech
        let fallback = activeTranslations[DEFAULT_LANGUAGE];
        for (const fk of keys) {
          if (fallback && typeof fallback === 'object' && fk in fallback) {
            fallback = fallback[fk];
          } else {
            return key;
          }
        }
        return fallback;
      }
    }
    return value;
  };

  return (
    <LanguageContext.Provider
      value={{ language, setLanguage, t, languages: LANGUAGES, refreshTranslations }}
    >
      {children}
    </LanguageContext.Provider>
  );
};
